// ============================================================================
// BookingFormModal — create or edit a resource booking.
// ============================================================================
// Used by AgcCalendar. Handles both create (no bookingId) and edit (with
// bookingId). Server enforces the actual access rules — AGC can only edit
// their own bookings — so this component just renders the form and surfaces
// any API errors.
//
// Times: HTML datetime-local fields, sent as ISO Z (we treat the wall-clock
// value as Central, same naive-timestamp convention used elsewhere).
// ============================================================================

import { useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Modal } from './Modal';
import { api, type SharedResource, type BookingStatus } from '../api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  token: string;
  resources: SharedResource[];
  // When set, edit mode. When null, create mode.
  bookingId?: number | null;
  initialResourceId?: number | null;
  initialStart?: string | null;     // ISO
  initialEnd?: string | null;       // ISO
  initialNotes?: string | null;
  initialStatus?: BookingStatus | null;
  // Hide delete when caller can't archive (AGC viewing UIF, or anonymous).
  canDelete?: boolean;
  // When false the form is read-only: all inputs disabled, Save button hidden,
  // only Close remains. UIF can edit anything; AGC can only edit AGC bookings.
  canEdit?: boolean;
}

function toLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 16);
}

function toISO(local: string): string {
  if (!local) return '';
  const withSeconds = local.length === 16 ? `${local}:00` : local;
  return `${withSeconds}Z`;
}

export function BookingFormModal({
  open,
  onClose,
  onSaved,
  token,
  resources,
  bookingId,
  initialResourceId,
  initialStart,
  initialEnd,
  initialNotes,
  initialStatus,
  canDelete,
  canEdit = true,
}: Props) {
  // When read-only, disabled cascade affects every input + button.
  const ro = !canEdit;
  const isEdit = bookingId != null;

  const [resourceId, setResourceId] = useState<number | ''>('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<BookingStatus>('confirmed');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  // Reset state on open so reuse across multiple clicks doesn't leak data.
  useEffect(() => {
    if (!open) return;
    setResourceId(initialResourceId ?? (resources[0]?.resource_id ?? ''));
    setStart(toLocal(initialStart));
    setEnd(toLocal(initialEnd));
    setNotes(initialNotes ?? '');
    setStatus((initialStatus ?? 'confirmed') as BookingStatus);
    setError('');
    setSubmitting(false);
  }, [open, initialResourceId, initialStart, initialEnd, initialNotes, initialStatus, resources]);

  const handleSave = async () => {
    if (!resourceId || !start || !end) {
      setError('Pick a resource and a start/end time.');
      return;
    }
    if (new Date(end) <= new Date(start)) {
      setError('End must be after start.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (isEdit && bookingId != null) {
        await api.updateBooking(token, bookingId, {
          start: toISO(start),
          end: toISO(end),
          booking_status: status,
          notes: notes.trim() || null,
        });
      } else {
        await api.createBooking(token, {
          resource_id: Number(resourceId),
          start: toISO(start),
          end: toISO(end),
          booking_status: status,
          notes: notes.trim() || null,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not save booking.');
      // The modal is short enough that scroll-to-top isn't usually needed,
      // but if the textarea pushes content off, this nudges the error into
      // view inside the Modal's scroll container.
      const banner = document.querySelector('[data-error-banner]');
      banner?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || bookingId == null) return;
    const yes = window.confirm('Cancel this booking? The slot will reopen on the calendar.');
    if (!yes) return;
    setSubmitting(true);
    try {
      await api.archiveBooking(token, bookingId);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not cancel booking.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? (ro ? 'Booking details' : 'Edit booking') : 'Reserve the room'} maxWidth="max-w-lg">
      <div className="space-y-4">
        {ro && (
          <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700">
            Read-only — this booking belongs to another organization. Contact UIF to request changes.
          </div>
        )}
        {error && (
          <div data-error-banner className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
            {error}
          </div>
        )}

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Resource</span>
          <select
            value={resourceId}
            onChange={(e) => setResourceId(e.target.value ? Number(e.target.value) : '')}
            disabled={submitting || isEdit}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none disabled:bg-gray-50"
          >
            <option value="">— Pick a room —</option>
            {/* Filter out staff resources — AGC only books rooms, not people.
                (Staff resources exist for internal event assignment; a services
                catalog for external clients is Task #F.) */}
            {resources
              .filter((r) => r.resource_type !== 'staff')
              .map((r) => (
                <option key={r.resource_id} value={r.resource_id}>
                  {r.resource_name}{r.capacity ? ` (cap. ${r.capacity})` : ''}
                </option>
              ))}
          </select>
          {isEdit && (
            <p className="text-[11px] text-gray-500 mt-1">
              Move to a different room? Cancel this booking and create a new one.
            </p>
          )}
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Start</span>
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              disabled={submitting || ro}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">End</span>
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              disabled={submitting || ro}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
          </label>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as BookingStatus)}
            disabled={submitting || ro}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          >
            <option value="confirmed">Confirmed — locks the room</option>
            <option value="proposed">Proposed — held tentatively</option>
            <option value="blackout">Blackout — room unavailable</option>
          </select>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting || ro}
            placeholder="What this booking is for"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>

        <div className="flex justify-between items-center pt-2">
          {isEdit && canDelete ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting || ro}
              className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Cancel booking
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting || ro}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Close
            </button>
            {!ro && (
              <button
                type="button"
                onClick={handleSave}
                disabled={submitting}
                className="inline-flex items-center gap-1 rounded-md bg-uif-green text-white px-4 py-1.5 text-sm font-semibold hover:bg-uif-green-dark disabled:bg-gray-300"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? 'Save changes' : 'Reserve'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
